// Дебиторка — первый сервис, который CRM забирает у порталов (веха В5).
//
// Экран ТОЛЬКО ЧИТАЮЩИЙ, и это не «пока не успели». Любая запись в смарт-процесс 134
// перезапускает его бизнес-процессы — в частности «Окраску платежей», которая двигает стадию
// (`БИЗНЕС-ПРОЦЕССЫ_Bitrix.md`, раздел про 134). Правку даты оплаты и комментариев делаем
// отдельной задачей, вместе с write-through в ядре и перечиткой записи после сохранения.
//
// Правила расчётов (что считать оплаченным, остатком, просрочкой) вынесены в lib/payments.ts:
// у порталов 55 и exec они разные, и здесь не должно появиться третьей версии.

// С 30.07.2026 у раздела ДВА ИСТОЧНИКА, переключатель явный и обратимый (?src= в
// адресе, по образцу /docs): 'mirror' — зеркало 134 (умолчание, как было), 'own' —
// своя схема (invoice + payment, ветка recv_own_api ядра). Единицы счёта у источников
// РАЗНЫЕ (записи «Платежи» против непогашенных счетов), поэтому own-ветка живёт
// отдельным экраном (ReceivablesOwn), а не флагом внутри этого: один экран на два
// набора оговорок однажды показал бы оговорку не того источника.

import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useEntityTitle, useFields, usePayments, useRefs } from '../api/hooks'
import type { ListParams } from '../api/types'
import { EntityPicker } from '../components/EntityPicker'
import { FilterBar } from '../components/FilterBar'
import { PaymentsTable } from '../components/PaymentsTable'
import { QueryState } from '../components/QueryState'
import type { EntityRef } from '../lib/entities'
import { fmtMoney, plural } from '../lib/format'
import {
  BUCKETS, PAID_CATEGORY_ID, PAY, PAY_BP_WRITTEN, PAY_EDITABLE_LATER, PAY_ENTITY,
  PAY_LIST_SELECT, PAY_TYPE_ID, paymentView, summarize,
} from '../lib/payments'
import ReceivablesOwn from './ReceivablesOwn'

const PAGE = 50   // страница ядра (PAGE в core_api) — как в списке сделок

type SortKey = 'due' | 'updated'

/** Источники раздела. Список закрытый: мусор в ?src= не должен молча стать зеркалом. */
const ИСТОЧНИКИ = ['mirror', 'own'] as const
type Источник = (typeof ИСТОЧНИКИ)[number]

/**
 * Переключатель источника. Живёт в адресе (?src=), а не в состоянии: ссылку «посмотри
 * в своей схеме» должно быть можно переслать — тот же приём, что у компании ниже.
 */
function SourceSwitch({ src, onChange }: { src: Источник; onChange: (v: Источник) => void }) {
  return (
    <label className="muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      источник
      <select value={src} aria-label="Источник данных"
              onChange={(e) => onChange(e.target.value as Источник)}>
        <option value="mirror">зеркало Bitrix (платежи 134)</option>
        <option value="own">своя схема (счета)</option>
      </select>
    </label>
  )
}

/**
 * Ветвление по источнику — ОТДЕЛЬНЫМ родителем, а не if внутри одного компонента:
 * у веток разные наборы хуков, и ранний return между хуками ломал бы их порядок при
 * переключении. Родитель читает только адрес; каждая ветка — свой компонент, и при
 * смене источника React их честно перемонтирует.
 */
export default function Receivables() {
  const [sp, setSp] = useSearchParams()

  const сыройSrc = (sp.get('src') || '').trim().toLowerCase()
  const src: Источник = (ИСТОЧНИКИ as readonly string[]).includes(сыройSrc)
    ? (сыройSrc as Источник) : 'mirror'
  // Мусор в src не подменяется молча: показываем зеркало, но ГОВОРИМ об этом —
  // молчаливая подмена однажды сравнила бы источник сам с собой (правило /docs).
  const srcНеПонят = !!сыройSrc && !(ИСТОЧНИКИ as readonly string[]).includes(сыройSrc)

  const changeSrc = (v: Источник) => {
    const next = new URLSearchParams(sp)
    if (v === 'mirror') next.delete('src')
    else next.set('src', v)
    setSp(next, { replace: true })
  }

  const switcher = <SourceSwitch src={src} onChange={changeSrc} />
  if (src === 'own') return <ReceivablesOwn switcher={switcher} />
  return <ReceivablesMirror switcher={switcher} srcНеПонят={srcНеПонят ? сыройSrc : ''} />
}

function ReceivablesMirror({ switcher, srcНеПонят }: {
  switcher: React.ReactNode
  /** Непустое — в адресе был неизвестный ?src=, показано зеркало; экран это печатает. */
  srcНеПонят: string
}) {
  const [sp, setSp] = useSearchParams()
  // Компанию читаем из адреса: из карточки компании сюда ведёт ссылка «показать все».
  const companyId = sp.get('company') || ''

  const [stage, setStage] = useState('')
  const [category, setCategory] = useState('')
  const [assigned, setAssigned] = useState('')
  const [onlyDebt, setOnlyDebt] = useState(true)
  // «Скрыть пробные» выключено по умолчанию намеренно. Пробные записи заводят в боевом
  // Bitrix при обкатке бизнес-процессов и обучении, чистить их нельзя, но и молча вычитать
  // из долга нельзя тоже: цифра экрана обязана по умолчанию совпадать с Bitrix и с порталами.
  // Поэтому сначала — пометка и оговорка, и только по желанию человека — сокрытие.
  const [hidePilot, setHidePilot] = useState(false)
  const [sort, setSort] = useState<SortKey>('due')
  const [start, setStart] = useState(0)

  const refs = useRefs()
  const f = useFields(PAY_ENTITY)

  const stageOptions = useMemo(
    () => refs.itemStageOptions(PAY_TYPE_ID, category), [refs, category],
  )
  const categories = useMemo(() => refs.itemCategories(PAY_TYPE_ID), [refs])

  // Название выбранной компании: один запрос на компанию (кэш на 10 минут), а не на строку.
  const companyTitle = useEntityTitle('company', companyId || undefined)
  const companyValue: EntityRef | null = companyId
    ? { kind: 'company', id: companyId, title: companyTitle.data || `#${companyId}` }
    : null

  const params = useMemo<ListParams>(() => {
    const filter: Record<string, unknown> = {}
    if (companyId) filter.companyId = companyId
    if (category) filter.categoryId = category
    if (stage) filter.stageId = stage          // у смарт-процессов stageId всегда с префиксом
    if (assigned) filter.assignedById = assigned
    if (onlyDebt) {
      // Ядро умеет только И, а «оплачен» в Bitrix определяется четырьмя независимыми
      // признаками (карта полей §5). Фильтром отсекаем два, которые выражаются условием
      // равенства: пустая дата оплаты и воронка не «Оплачено». Флаг «Оплачен» и стадию
      // «успех» ядро отфильтровать не может — такие строки приходят и помечаются в таблице.
      filter[PAY.paidDate] = ''
      if (category !== PAID_CATEGORY_ID) filter['!categoryId'] = PAID_CATEGORY_ID
    }
    // Сортировка по сроку — по UF-полю: в ядре это текст, но ISO-дата сортируется как дата.
    // Записи без срока при этом расходятся по краям: с пустым значением — в начало,
    // с отсутствующим полем — в конец (NULLS LAST у Postgres). Поэтому есть второй режим,
    // по изменению, как в списке сделок.
    const order: Record<string, 'ASC' | 'DESC'> = sort === 'due'
      ? { [PAY.dueDate]: 'ASC' }
      : { updatedTime: 'DESC' }
    return { filter, order, select: PAY_LIST_SELECT, start }
  }, [companyId, category, stage, assigned, onlyDebt, sort, start])

  const q = usePayments(params)
  const data = q.data

  const allRows = useMemo(
    () => (data?.items ?? []).map((p) => paymentView(p, refs)),
    [data, refs],
  )
  // Сокрытие — только на загруженной странице: ядро про «пробность» ничего не знает,
  // фильтровать в запросе нечем. Значит и постраничность остаётся прежней: на странице
  // станет меньше строк, а не «страницы поедут» — счётчик `data.total` мы не трогаем.
  const rows = useMemo(
    () => (hidePilot ? allRows.filter((r) => !r.pilot) : allRows),
    [allRows, hidePilot],
  )
  const totals = useMemo(() => summarize(rows), [rows])
  /** Сколько пробных на странице всего — считаем до сокрытия, иначе после включения ноль. */
  const pilotOnPage = useMemo(() => allRows.filter((r) => r.pilot).length, [allRows])

  // смена любого фильтра — всегда с первой страницы
  const resetAnd = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  const changeCategory = (v: string) => {
    setStart(0)
    setCategory(v)
    // у другой воронки другие стадии — выбранная повисла бы и не нашла ничего
    if (v && stage && !refs.itemStageOptions(PAY_TYPE_ID, v).some((o) => o.id === stage)) setStage('')
  }

  const changeCompany = (v: EntityRef | null) => {
    setStart(0)
    // компания живёт в адресе, чтобы ссылку на «дебиторку клиента» можно было переслать;
    // копию делаем намеренно — правка sp на месте не считается изменением состояния
    const next = new URLSearchParams(sp)
    if (v) next.set('company', v.id)
    else next.delete('company')
    setSp(next, { replace: true })
  }

  return (
    <>
      <div className="page-head">
        <h1>Дебиторка</h1>
        {switcher}
        {/* «в вашей зоне» стоит у самого числа: счётчик читают отдельно от оговорок, и
            «платежей: 43» без этой приписки означало бы «вся дебиторка — 43 платежа». */}
        <span className="muted">
          {data && !data.stale
            ? `платежей: ${data.total}${data.scope?.limited ? ' (в вашей зоне)' : ''}`
            : ''}
        </span>
      </div>

      {srcНеПонят && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="hint">
            Источник «{srcНеПонят}» не существует: бывает mirror (зеркало Bitrix) и own
            (своя схема). Показано зеркало — как если бы источник не был указан.
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="receivables">
          <div style={{ flex: 2, minWidth: 220 }}>
            <EntityPicker
              kind="company" id="pay-company" label="Компания"
              value={companyValue} onChange={changeCompany}
            />
          </div>

          <div className="f">
            <label htmlFor="pay-cat">Воронка</label>
            <select id="pay-cat" value={category} onChange={(e) => changeCategory(e.target.value)}
                    disabled={categories.length === 0}>
              <option value="">любая</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="f" style={{ minWidth: 190 }}>
            <label htmlFor="pay-stage">Стадия</label>
            <select id="pay-stage" value={stage} onChange={(e) => resetAnd(setStage)(e.target.value)}
                    disabled={stageOptions.length === 0}>
              <option value="">любая</option>
              {stageOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.categoryName ? `${o.name} · ${o.categoryName}` : o.name}
                </option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 190 }}>
            <label htmlFor="pay-resp">Ответственный</label>
            <select id="pay-resp" value={assigned}
                    onChange={(e) => resetAnd(setAssigned)(e.target.value)}
                    disabled={refs.users.length === 0}>
              <option value="">любой</option>
              {refs.users.map((u) => (
                <option key={u.id} value={u.id}>{u.active ? u.name : `${u.name} (уволен)`}</option>
              ))}
            </select>
          </div>

          <div className="f">
            <label htmlFor="pay-sort">Сортировка</label>
            <select id="pay-sort" value={sort}
                    onChange={(e) => resetAnd(setSort)(e.target.value as SortKey)}>
              <option value="due">по сроку оплаты</option>
              <option value="updated">по изменению</option>
            </select>
          </div>

          <div className="f" style={{ minWidth: 160 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
              <input type="checkbox" checked={onlyDebt} style={{ width: 'auto' }}
                     onChange={(e) => resetAnd(setOnlyDebt)(e.target.checked)} />
              только неоплаченные
            </label>
          </div>

          <div className="f" style={{ minWidth: 160 }}>
            {/* Страницу не сбрасываем: сокрытие работает на уже загруженных строках,
                возврат на первую страницу выглядел бы как смена выборки, а её нет. */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}
                   title="Пробные записи остаются в Bitrix и в дебиторке — выключатель прячет их только здесь">
              <input type="checkbox" checked={hidePilot} style={{ width: 'auto' }}
                     onChange={(e) => setHidePilot(e.target.checked)} />
              скрыть пробные
            </label>
          </div>
        </FilterBar>

        {onlyDebt && (
          <div className="hint" style={{ marginTop: 10 }}>
            «Неоплаченные» ядро отбирает по пустой дате оплаты и воронке, не равной «Оплачено».
            Платежи, помеченные оплаченными только флагом или стадией, ядро отфильтровать не
            умеет — они приходят и помечены в списке значком «оплачен».
          </div>
        )}
        {!refs.hasItemStages(PAY_TYPE_ID) && (
          <div className="hint" style={{ marginTop: 10 }}>
            Справочник стадий смарт-процесса 134 из ядра не пришёл: стадии показаны сырыми
            кодами Bitrix, фильтры по стадии и воронке отключены, а «оплачено по стадии»
            определяется только для стандартных SUCCESS/FAIL.
          </div>
        )}
        {refs.loaded && refs.dictsStale && (
          <div className="hint" style={{ marginTop: 10 }}>
            Воронки и стадии — из старого кеша ядра (Bitrix не ответил). Названия могут отставать.
          </div>
        )}
      </div>

      <QueryState isLoading={q.isLoading} error={q.error} stale={data?.stale} ageMin={data?.ageMin}
                  scope={data?.scope}>
        {/*
          Пока ядро отдаёт устаревшие данные, цифр здесь нет вовсе — ни итога, ни «ничего не
          найдено». Раньше рядом с пометкой «данным верить нельзя» на экране стояли «Итого
          долг 0 ₽» и «Ничего не найдено под эти фильтры»: три взаимоисключающих утверждения,
          из которых человек выбирает привычное — ноль. Правило то же, что в списке сделок
          (`data && !data.stale`): не знаем — не показываем цифру, а объясняем, почему её нет.
        */}
        {!data || data.stale ? (
          <div className="card">
            <div className="muted">
              Итоги и список платежей не показаны: ядро предупредило, что данные устарели.
              Долг по устаревшей выборке — это не «долгов нет» и не «долг такой»; правильной
              цифры у нас сейчас нет, и подставить вместо неё ноль было бы хуже пустого места.
              Обновите страницу, когда синк догонит (состояние синка — в разделе «Здоровье»).
            </div>
          </div>
        ) : (
        <>
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="page-head" style={{ marginBottom: 10 }}>
            <h2 style={{ margin: 0 }}>Корзины срочности</h2>
            <div className="spacer" />
            <span className="hint">
              по показанным {rows.length}{' '}
              {plural(rows.length, 'строке', 'строкам', 'строкам')} из {data.total}
            </span>
          </div>

          <div className="tiles">
            {BUCKETS.map((b) => (
              <div key={b.key} className={`tile${b.hot && totals.buckets[b.key].count ? ' hot' : ''}`}
                   title={b.hint}>
                <div className="t-label">{b.label}</div>
                <div className="t-sum">{fmtMoney(totals.buckets[b.key].sum)}</div>
                <div className="t-label">{totals.buckets[b.key].count} шт.</div>
              </div>
            ))}
            <div className="tile">
              <div className="t-label">Итого долг</div>
              <div className="t-sum">{fmtMoney(totals.total.sum)}</div>
              <div className="t-label">{totals.total.count} шт.</div>
            </div>
          </div>

          {/* Итоги считаются по загруженной странице: агрегата у ядра нет, а тянуть все
              16 тысяч платежей на клиент — 330 запросов. Признаём это прямо, чтобы никто
              не принял сумму страницы за сумму дебиторки. */}
          <div className="hint" style={{ marginTop: 10 }}>
            Суммы — по загруженной странице, не по всей дебиторке: агрегатов ядро пока не отдаёт
            (нужен SQL с группировкой, а не выкачивание 16 тысяч записей).
            {totals.paid.count > 0 && ` Оплаченных на странице: ${totals.paid.count} — в корзины они не входят.`}
            {totals.failed.count > 0 && ` Списанных (стадия «провал»): ${totals.failed.count}.`}
            {totals.dirty > 0 && ` С противоречивыми признаками оплаты: ${totals.dirty} — помечены «?».`}
            {/* Пробные записи названы прямо и с суммой: без этой строки человек видит в
                дебиторке долг на 1 ₽ и не понимает, ошибка это, копейки или чья-то отладка.
                Сумму печатаем даже когда она мала — «мало» должен решать читатель. */}
            {!hidePilot && totals.pilot.count > 0 && (
              ` Из них похоже на пробные записи (обкатка бизнес-процессов, обучение):`
              + ` ${totals.pilot.count} на ${fmtMoney(totals.pilot.sum)} — помечены «пробная»,`
              + ` в итог включены. В Bitrix они остаются: чистить их мы не вправе.`
            )}
            {hidePilot && (
              pilotOnPage > 0
                ? ` Пробные записи скрыты: ${pilotOnPage} шт. на этой странице не показаны и`
                  + ` в итоги не вошли. В Bitrix они остались, и долг там больше на эту сумму.`
                : ' Пробные записи скрыты, но на этой странице их нет — итоги не изменились.'
            )}
            {/* Второе сужение, поверх страничного: права роли. «Итого долг» тогда — долг
                вашей зоны, и звонить клиенту по этой цифре как по всему долгу нельзя. */}
            {data.scope?.limited && (
              ' Кроме того, выборка сужена вашими правами: «Итого долг» — это долг по вашей'
              + ' зоне, а не долг компании'
              + (data.scope.note ? ` (${data.scope.note})` : '') + '.'
            )}
          </div>
        </div>

        <div className="card">
          <PaymentsTable
            rows={rows}
            emptyText={data.scope?.limited
              // «в вашей зоне платежей нет» — не то же, что «платежей нет»: в первом случае
              // они существуют, просто не ваши.
              ? 'В вашей зоне таких платежей нет — выборка сужена правами, а не фильтрами'
              : 'Ничего не найдено под эти фильтры'}
          />

          {data.total > PAGE && (
            <div className="pager">
              <button disabled={start === 0 || q.isFetching}
                      onClick={() => setStart(Math.max(0, start - PAGE))}>
                ← назад
              </button>
              <span className="muted">
                {start + 1}–{Math.min(start + PAGE, data.total)} из {data.total}
              </span>
              <button disabled={data.next === undefined || q.isFetching}
                      onClick={() => setStart(data.next!)}>
                вперёд →
              </button>
            </div>
          )}
        </div>
        </>
        )}
      </QueryState>

      <div className="card">
        <h2>Правка платежей: почему её здесь нет</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Запись в платёж перезапускает бизнес-процессы смарт-процесса 134 — в том числе
          «Окраску платежей», которая двигает стадию. Поэтому в этой вехе экран только читает.
          Стадия платежа не редактируется вообще: это корзина по сроку, её ставит робот.
        </p>
        <dl className="dl">
          {PAY_EDITABLE_LATER.map((code) => (
            <div key={code} style={{ display: 'contents' }}>
              <dt>{f.meta(code) ? f.title(code) : code}</dt>
              <dd>
                {f.readonly(code)
                  ? <span className="badge lose">только чтение по справочнику ядра</span>
                  : <span className="badge">правка — следующей задачей (write-through)</span>}
              </dd>
            </div>
          ))}
          {PAY_BP_WRITTEN.map((code) => (
            <div key={code} style={{ display: 'contents' }}>
              <dt>{f.meta(code) ? f.title(code) : code}</dt>
              <dd>
                <span className="badge lose">
                  только чтение{f.meta(code)?.bp_computed ? ': значение считает бизнес-процесс' : ''}
                </span>
              </dd>
            </div>
          ))}
        </dl>
        {!f.loaded && (
          <div className="hint" style={{ marginTop: 10 }}>
            Справочник полей ядра недоступен — названия показаны кодами Bitrix, а пометки
            «только чтение» взяты не из данных, а из карты полей.
          </div>
        )}
      </div>
    </>
  )
}
